/**
 * Utility for observing DOM elements across document and iframes
 */

import { iframeready } from "./utils";

type CleanupFunction = () => void;
type DocumentHandler<T> = (doc: Document) => T;

/**
 * Safely executes a document handler, catching any errors
 */
function safeDocHandler<T extends CleanupFunction>(
  docHandler: DocumentHandler<T>,
  doc: Document,
  cleanupFunctions: CleanupFunction[]
): void {
  try {
    const cleanup = docHandler(doc);
    if (typeof cleanup === "function") {
      cleanupFunctions.push(cleanup);
    }
  } catch (e) {
    // Log but don't throw - one document failure shouldn't break others
    console.warn("[Syft] Error handling document:", e);
  }
}

/**
 * Observes the main document and all iframe documents, applying the provided handler
 * to each document. Returns a cleanup function that removes all observers/handlers.
 *
 * Cross-origin iframes are automatically skipped as they cannot be accessed.
 *
 * @param docHandler Function to apply to each document (main and iframe)
 * @param observeNewIframes Whether to observe for new iframes being added to the DOM
 * @returns Cleanup function that removes all handlers
 */
export function observeDocuments<T extends CleanupFunction>(
  docHandler: DocumentHandler<T>,
  observeNewIframes = true
): CleanupFunction {
  const cleanupFunctions: CleanupFunction[] = [];

  // Handle main document
  safeDocHandler(docHandler, document, cleanupFunctions);

  // Handle existing iframes (iframeready handles cross-origin safely)
  document.querySelectorAll("iframe").forEach((iframe) => {
    iframeready(iframe, (doc) => {
      safeDocHandler(docHandler, doc, cleanupFunctions);
    });
  });

  // Observe for new iframes if requested
  let observer: MutationObserver | undefined;

  if (observeNewIframes) {
    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLIFrameElement) {
              iframeready(node, (doc) => {
                safeDocHandler(docHandler, doc, cleanupFunctions);
              });
            }
            // Also check for iframes nested within added nodes
            else if (node instanceof Element) {
              node.querySelectorAll("iframe").forEach((iframe) => {
                iframeready(iframe, (doc) => {
                  safeDocHandler(docHandler, doc, cleanupFunctions);
                });
              });
            }
          });
        }
      });
    });

    observer.observe(document, { childList: true, subtree: true });
  }

  // Return cleanup function
  return () => {
    cleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch {
        // Cleanup failed - document may have been removed
      }
    });
    if (observer) observer.disconnect();
  };
}
